import React, { useState, useEffect } from "react";
import DataTable from "../DataTable";
import Switch from "react-switch";
import CustomSwitch from "../CommonComponents/CustomSwitch";
import AddEmployeeForm from "./AddEmployeeForm";
import EditEmployeeForm from "./EditEmployeeForm";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuthz } from "../../auth/useAuthz";
const EMPLOYEE_ACTIVE = gql`
  mutation updateEmployeeActive($data: empActive!) {
    updateEmployeeActive(data: $data) {
      status
      message
    }
  }
`;

const EMPLOYEE_EMPLOYMENT = gql`
  mutation updateEmployeeEmployment($data: EmployeeEmploymentInput!) {
    updateEmployeeEmployment(data: $data) {
      status
      message
    }
  }
`;

const EMPLOYEE_DUMMY = gql`
  mutation updateEmployeeDummy($data: empDummy!) {
    updateEmployeeDummy(data: $data) {
      status
      message
    }
  }
`;

const EMPLOYEE_NOTIFY_ADMIN = gql`
  mutation updateEmployeeNotifyAdmin($data: empNotifyAdmin!) {
    updateEmployeeNotifyAdmin(data: $data) {
      status
      message
    }
  }
`;

const EmployeeManagementTable = ({
  data,
  addEmployee,
  setAddEmployee,
  editForm,
  setEditForm,
  editData,
  setEditData,
  isFetched,
}) => {
  const navigate = useNavigate();
  const { can } = useAuthz();

  const [employeeList, setEmployeeList] = useState(data);
  const [updatingRowId, setUpdatingRowId] = useState(null);
  const [updatingDummyRowId, setUpdatingDummyRowId] = useState(null);
  const [updatingNotifyRowId, setUpdatingNotifyRowId] = useState(null);

  //api calls
  const [changeEmpStatus, { error: statusError, loading: statusLoading }] =
    useMutation(EMPLOYEE_ACTIVE);
  const [changeEmpDummy, { loading: dummyLoading }] =
    useMutation(EMPLOYEE_DUMMY);
  const [changeEmpNotifyAdmin, { loading: notifyLoading }] = useMutation(
    EMPLOYEE_NOTIFY_ADMIN
  );

  // helper function
  const handleEditClick = (data) => {
    setEditData(data);

    setEditForm(true);
  };
  const handleDashboardClick = (employeeData) => {
    navigate(`/admin/employee-dashboard/${employeeData.id}`, {
      state: { employeeData },
    });
  };

  // Two switches: "Bookings" (isOnBoarded) opens or closes their slots;
  // "Employed" says whether they still work here. Marking someone as left
  // asks for their last working day - payroll pays them up to it.
  const [leavingFor, setLeavingFor] = useState(null);
  const [leavingDate, setLeavingDate] = useState("");
  const [stopBookings, setStopBookings] = useState(true);
  const [changeEmployment] = useMutation(EMPLOYEE_EMPLOYMENT);
  const todayIST = () =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

  const handleToggle = async (row) => {
    setUpdatingRowId(row.id); // mark which row is updating

    try {
      const res = await changeEmpStatus({
        variables: {
          data: {
            id: row.id,
            isOnBoarded: !row.isOnBoarded,
          },
        },
      });

      if (res.data.updateEmployeeActive.status === "SUCCESS") {
        // Update local list here instead of full reload
        setEmployeeList((prev) =>
          prev.map((emp) =>
            emp.id === row.id ? { ...emp, isOnBoarded: !emp.isOnBoarded } : emp
          )
        );
      } else {
        alert(res.data.updateEmployeeActive.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingRowId(null); // reset after done
    }
  };

  const handleEmployedToggle = (row) => {
    if (row.employed) {
      setLeavingDate(todayIST());
      // A dummy profile stays bookable by default
      setStopBookings(!row.isDummy);
      setLeavingFor(row);
      return;
    }
    if (
      !window.confirm(
        `Mark ${row.fullName} as employed again?${
          row.leavingDate ? ` Their leaving date (${row.leavingDate}) will be cleared.` : ""
        }`
      )
    ) {
      return;
    }
    saveEmployment(row, true);
  };

  const saveEmployment = async (row, employed) => {
    try {
      const res = await changeEmployment({
        variables: {
          data: {
            id: row.id,
            employed,
            ...(employed ? {} : { leavingDate, stopBookings }),
          },
        },
      });
      const out = res.data.updateEmployeeEmployment;
      if (out.status === "SUCCESS") {
        setEmployeeList((prev) =>
          prev.map((emp) =>
            emp.id === row.id
              ? {
                  ...emp,
                  employed,
                  leavingDate: employed ? null : leavingDate,
                  isOnBoarded: !employed && stopBookings ? false : emp.isOnBoarded,
                }
              : emp
          )
        );
      } else {
        alert(out.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDummyToggle = async (row) => {
    setUpdatingDummyRowId(row.id);

    try {
      const res = await changeEmpDummy({
        variables: {
          data: {
            id: row.id,
            isDummy: !row.isDummy,
          },
        },
      });

      if (res.data.updateEmployeeDummy.status === "SUCCESS") {
        setEmployeeList((prev) =>
          prev.map((emp) =>
            emp.id === row.id ? { ...emp, isDummy: !emp.isDummy } : emp
          )
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingDummyRowId(null);
    }
  };

  const handleNotifyAdminToggle = async (row) => {
    setUpdatingNotifyRowId(row.id);

    try {
      const res = await changeEmpNotifyAdmin({
        variables: {
          data: {
            id: row.id,
            notifyAdmin: !row.notifyAdmin,
          },
        },
      });

      if (res.data.updateEmployeeNotifyAdmin.status === "SUCCESS") {
        setEmployeeList((prev) =>
          prev.map((emp) =>
            emp.id === row.id ? { ...emp, notifyAdmin: !emp.notifyAdmin } : emp
          )
        );
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingNotifyRowId(null);
    }
  };

  //
  return (
    <>
      {addEmployee ? (
        <AddEmployeeForm setAddEmployee={setAddEmployee} />
      ) : editForm ? (
        <EditEmployeeForm setEditForm={setEditForm} editData={editData} />
      ) : (
        <div>
          {isFetched && (
            <div className="hidden md:block">
            <DataTable tableName="Employee-managment">
              <thead>
                <tr>
                  <th data-priority="1" className="text-white">
                    Name
                  </th>

                  <th data-priority="4" className="text-white">
                    Mobile Number
                  </th>
                  <th data-priority="5" className="text-white" title="Takes bookings - opens or closes their slots">
                    Bookings
                  </th>

                  <th data-priority="5" className="text-white" title="Works here - payroll, leave, attendance">
                    Employed
                  </th>

                  <th data-priority="5" className="text-white">
                    Dummy
                  </th>

                  <th data-priority="5" className="text-white">
                    Notify Admin
                  </th>

                  <th data-priority="6" className="text-white">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* <tr> */}
                {employeeList.map((row, i) => {
                  return (
                    //
                    <tr
                      className="border-b-2 border-b-[#E0E0E0] hover:cursor-pointer"
                      key={row.id}
                    >
                      <td className="text-white w-[10%] px-1 border-b-[1px] border-b-[#E0E0E0]">
                        {row.fullName}
                      </td>

                      <td className="text-white w-[7%] text-center border-b-[1px] border-b-[#E0E0E0]">{`${row.contactNumber}`}</td>
                      <td className="text-white w-[7%] text-center border-b-[1px] border-b-[#E0E0E0]">
                        <CustomSwitch
                          checked={row.isOnBoarded}
                          isLoading={statusLoading && i === row.id}
                          onChange={() => {
                            if (can("employeeManagement.edit")) {
                              handleToggle(row);
                            }
                          }}
                          id={row.id}
                        />
                      </td>

                      <td className="text-white w-[7%] text-center border-b-[1px] border-b-[#E0E0E0]">
                        <CustomSwitch
                          checked={Boolean(row.employed)}
                          onChange={() => {
                            if (can("employeeManagement.edit")) {
                              handleEmployedToggle(row);
                            }
                          }}
                          id={`employed-${row.id}`}
                        />
                        {!row.employed && row.leavingDate && (
                          <div className="text-[10px] text-gray-400 mt-1">left {row.leavingDate}</div>
                        )}
                      </td>

                      <td className="text-white w-[7%] text-center border-b-[1px] border-b-[#E0E0E0]">
                        <CustomSwitch
                          checked={row.isDummy}
                          isLoading={dummyLoading && updatingDummyRowId === row.id}
                          onChange={() => {
                            if (can("employeeManagement.edit")) {
                              handleDummyToggle(row);
                            }
                          }}
                          id={`dummy-${row.id}`}
                        />
                      </td>

                      <td className="text-white w-[7%] text-center border-b-[1px] border-b-[#E0E0E0]">
                        <CustomSwitch
                          checked={row.notifyAdmin}
                          isLoading={notifyLoading && updatingNotifyRowId === row.id}
                          onChange={() => {
                            if (can("employeeManagement.edit")) {
                              handleNotifyAdminToggle(row);
                            }
                          }}
                          id={`notify-${row.id}`}
                        />
                      </td>

                      <td className="w-[10%] text-center px-2 border-b-[1px] border-b-[#E0E0E0]">
                        <div className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 items-center justify-center h-full lg:space-x-2">
                          {can("employeeManagement.edit") && (
                            <button
                              className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                              onClick={() => handleEditClick(row)}
                            >
                              Edit
                            </button>
                          )}
                          {can("employeeDashboard.read") && (
                            <button
                              className="rounded-md bg-white py-2 px-3 text-black text-sm font-medium"
                              onClick={() => handleDashboardClick(row)}
                            >
                              Dashboard
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </DataTable>
            </div>
          )}

          {/* Mobile: card view */}
          {isFetched && (
            <div className="md:hidden flex flex-col gap-3">
              {employeeList.map((row, i) => (
                <div
                  key={row.id}
                  className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-white font-semibold text-sm break-words">
                      {row.fullName}
                    </span>
                    <CustomSwitch
                      checked={row.isOnBoarded}
                      isLoading={statusLoading && i === row.id}
                      onChange={() => {
                        if (can("employeeManagement.edit")) {
                          handleToggle(row);
                        }
                      }}
                      id={`mobile-${row.id}`}
                    />
                  </div>

                  <div className="mt-3 text-sm">
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Mobile Number
                    </div>
                    <div className="text-white">{row.contactNumber}</div>
                  </div>

                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Employed{!row.employed && row.leavingDate ? ` · left ${row.leavingDate}` : ""}
                    </span>
                    <CustomSwitch
                      checked={Boolean(row.employed)}
                      onChange={() => {
                        if (can("employeeManagement.edit")) {
                          handleEmployedToggle(row);
                        }
                      }}
                      id={`mobile-employed-${row.id}`}
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Dummy
                    </span>
                    <CustomSwitch
                      checked={row.isDummy}
                      isLoading={dummyLoading && updatingDummyRowId === row.id}
                      onChange={() => {
                        if (can("employeeManagement.edit")) {
                          handleDummyToggle(row);
                        }
                      }}
                      id={`mobile-dummy-${row.id}`}
                    />
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Notify Admin
                    </span>
                    <CustomSwitch
                      checked={row.notifyAdmin}
                      isLoading={notifyLoading && updatingNotifyRowId === row.id}
                      onChange={() => {
                        if (can("employeeManagement.edit")) {
                          handleNotifyAdminToggle(row);
                        }
                      }}
                      id={`mobile-notify-${row.id}`}
                    />
                  </div>

                  {(can("employeeManagement.edit") ||
                    can("employeeDashboard.read")) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2a2a]">
                      {can("employeeManagement.edit") && (
                        <button
                          className="flex-1 rounded-md bg-white py-1.5 px-3 text-black text-sm font-medium"
                          onClick={() => handleEditClick(row)}
                        >
                          Edit
                        </button>
                      )}
                      {can("employeeDashboard.read") && (
                        <button
                          className="flex-1 rounded-md bg-white py-1.5 px-3 text-black text-sm font-medium"
                          onClick={() => handleDashboardClick(row)}
                        >
                          Dashboard
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {leavingFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">
              {leavingFor.fullName} has left?
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Salary for that month is paid up to their last working day. After
              it they are out of payroll, leave and attendance.
            </p>
            <label className="text-sm text-gray-700">
              Last working day
              <input
                type="date"
                value={leavingDate}
                onChange={(e) => setLeavingDate(e.target.value)}
                className="block border rounded px-2 py-1 mt-1 w-full"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3">
              <input
                type="checkbox"
                checked={stopBookings}
                onChange={(e) => setStopBookings(e.target.checked)}
              />
              Also switch off bookings
            </label>
            {!stopBookings && (
              <p className="text-xs text-gray-500 mt-1">
                The profile stays bookable - mark it Dummy so its bookings are noticed.
              </p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setLeavingFor(null)}
                className="px-4 py-2 rounded border text-sm font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                disabled={!leavingDate}
                onClick={() => {
                  const row = leavingFor;
                  setLeavingFor(null);
                  saveEmployment(row, false);
                }}
                className="px-4 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                Mark as left
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeManagementTable;
